import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Checkbox, Chip, CircularProgress, Modal, Snackbar, Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { FiSearch } from 'react-icons/fi';
import DeleteIcon from '@mui/icons-material/Delete';
import { motion } from 'framer-motion';
import { BASE_URL } from '../../utils/endpoints';
import BrandCreate from './BrandCreate';
import BrandEdit from './BrandEdit';
import parse from 'html-react-parser'; // Import html-react-parser

const truncateText = (text, maxLength = 70) => {
    if (!text) return '';
    // Strip HTML tags for truncation to avoid breaking tags
    const plainText = text.replace(/<[^>]+>/g, '');
    return plainText.length > maxLength ? plainText.slice(0, maxLength) + '…' : plainText;
};

const BrandList = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalRows, setTotalRows] = useState(0);
    const [openEdit, setOpenEdit] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [confirmDelete, setConfirmDelete] = useState(false);

    const handleOpen = (brand) => {
        setSelectedBrand(brand);
        setOpenEdit(true);
    };

    const handleClose = () => {
        setOpenEdit(false);
        setSelectedBrand(null);
    };

    const handleOpenCreate = () => {
        setOpenCreate(true);
    };

    const handleCloseCreate = () => {
        setOpenCreate(false);
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleConfirmDeleteOpen = () => {
        if (selectedRows.length > 0) {
            setConfirmDelete(true);
        } else {
            setSnackbarMessage('Please select at least one brand to delete');
            setSnackbarSeverity('warning');
            setOpenSnackbar(true);
        }
    };

    const handleConfirmDeleteClose = () => {
        setConfirmDelete(false);
    };

    const handleBulkDelete = async () => {
        try {
            const deletePromises = selectedRows.map(id =>
                fetch(`${BASE_URL}/brands/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }).then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to delete brand with ID ${id}`);
                    }
                    return response;
                })
            );
            const responses = await Promise.all(deletePromises);
            const allSuccessful = responses.every(response => response.ok);

            if (allSuccessful) {
                setBrands(brands.filter(brand => !selectedRows.includes(brand.id)));
                setSelectedRows([]);
                setSnackbarMessage(`Successfully deleted ${selectedRows.length} brand(s)`);
                setSnackbarSeverity('success');
            } else {
                setSnackbarMessage('Failed to delete some brands');
                setSnackbarSeverity('error');
            }
        } catch (error) {
            console.error('Error deleting brands:', error);
            setSnackbarMessage('Error deleting brands: ' + error.message);
            setSnackbarSeverity('error');
        } finally {
            setOpenSnackbar(true);
            setConfirmDelete(false);
        }
    };

    const fetchBrands = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}/brands?page=${page + 1}&limit=${rowsPerPage}`);
            if (!response.ok) {
                throw new Error('Failed to fetch brands');
            }
            const data = await response.json();

            const formattedBrands = (data.brands || []).map(brand => ({
                id: brand._id || `fallback-${Math.random()}`,
                logo: brand.brand_logo || null,
                name: brand.brand_name || 'Unnamed Brand',
                cashbackRate: brand.cashback_rate || 0,
                offer: brand.client_offer || 'No offer',
                category: brand.category || 'Unknown Category',
                description: brand.description || 'No description available',
                visitLink: brand.visit_link || '#',
                calc_cashback: brand.calc_cashback || 0,
                cashback_claim_time: brand.cashback_claim_time || 'N/A',
                client_link: brand.client_link || '#',
                deals_description: brand.deals_description || 'No deals available',
                default_tracking: brand.default_tracking || false,
                estimated_cashback_date: brand.estimated_cashback_date || 'N/A',
                hashtags: brand.hashtags || [],
                meta_description: brand.meta_description || 'No meta description',
                meta_tags: brand.meta_tag || [],
                meta_title: brand.meta_title || 'No meta title',
                search_keywords: brand.search_keywords || [],
                status: brand.status || 'active',
                terms_conditions: brand.terms_conditions || 'No terms and conditions',
                tracking_speed: brand.tracking_speed || 'N/A',
                trending: brand.trending || false,
                user_show_cashback: brand.user_show_cashback || false,
            })).reverse();

            setBrands(formattedBrands);
            setTotalRows(data.total || formattedBrands.length);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBrands();
    }, [page, rowsPerPage]);

    const handleBrandUpdated = () => {
        fetchBrands();
    };

    const handleBrandCreated = () => {
        fetchBrands();
        handleCloseCreate();
    };

    const columns = [
        {
            field: 'select',
            headerName: '',
            width: 70,
            renderCell: (params) => (
                <Checkbox
                    checked={selectedRows.includes(params.row.id)}
                    onChange={(event) => {
                        setSelectedRows(
                            event.target.checked
                                ? [...selectedRows, params.row.id]
                                : selectedRows.filter(id => id !== params.row.id)
                        );
                    }}
                    sx={{
                        color: '#2e7d32',
                        '&.Mui-checked': {
                            color: '#2e7d32',
                        },
                    }}
                />
            ),
        },
        {
            field: 'name',
            headerName: <strong style={{ color: "#24803F" }}>Brand Name</strong>,
            flex: 1,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        bgcolor: '#f5f5f5',
                        height: '100px',
                        width: '100%'
                    }}>
                        <motion.img
                            src={params.row.logo || `https://via.placeholder.com/40?text=${params.row.name?.[0] || 'N'}`}
                            style={{ width: 40, height: 40, borderRadius: 1, marginRight: 16 }}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.4 }}
                        />
                        <Box
                            sx={{
                                display: 'flex', flexDirection: 'column',
                                justifyContent: "center",
                            }}
                        >
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600, cursor: 'pointer', color: '#2e7d32', fontSize: '16px', textDecoration: 'none',
                                        position: "absolute",
                                    }}
                                    onClick={() => handleOpen(params.row)}
                                >
                                    {truncateText(params.row.name || 'Unnamed Product')}
                                </Typography>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    <strong>Category:</strong> {params.row.category || 'Unknown Category'}
                                </Typography>
                            </motion.div>
                        </Box>
                    </Box>
                </motion.div>
            ),
        },
        {
            field: 'cashbackRate',
            headerName: <strong style={{ color: "#24803F" }}>Cashback Rate</strong>,
            width: 120,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                >
                    {`${params.value}%`}
                </motion.div>
            ),
        },
        {
            field: 'offer',
            headerName: <strong style={{ color: "#24803F" }}>Offer</strong>,
            width: 150,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                >
                    {truncateText(params.value)}
                </motion.div>
            ),
        },
        {
            field: 'description',
            headerName: <strong style={{ color: "#24803F" }}>Description</strong>,
            width: 320,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.4 }}
                    style={{ maxHeight: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {parse(params.value || 'No description available')}
                </motion.div>
            ),
        },
        {
            field: 'visitLink',
            headerName: <strong style={{ color: "#24803F" }}>Visit</strong>,
            width: 100,
            renderCell: (params) => (
                <motion.a
                    href={params.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.5 }}
                    whileHover={{ color: '#2e7d32', scale: 1.05 }}
                >
                    Visit
                </motion.a>
            ),
        },
    ];

    if (loading) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
    </Box>
            </motion.div>
        );
    }

if (error) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Box sx={{ mt: 8 }}>
                <Typography color="error">Error: {error}</Typography>
            </Box>
        </motion.div>
    );
}

return (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Box sx={{ mt: 3, borderRadius: 2, p: 3, bgcolor: '#fff', boxShadow: 1 }}>
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                    Brand
                </Typography>
                <Typography variant="body2" color="#2e7d32" sx={{ mb: 2 }}>
                    Dashboard • Brand • List
                </Typography>
            </motion.div>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            placeholder="Search..."
                            size="small"
                            InputProps={{
                                startAdornment: <FiSearch style={{ marginRight: 8, color: '#9ca3af' }} />,
                            }}
                        />
                    </Box>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{
                                borderColor: '#9ca3af',
                                color: '#9ca3af',
                                '&:hover': { borderColor: 'tomato', color: 'red' },
                            }}
                            startIcon={<DeleteIcon />}
                            onClick={handleConfirmDeleteOpen}
                            disabled={selectedRows.length === 0}
                        >
                            Delete Brand ({selectedRows.length})
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                backgroundColor: '#2e7d32',
                                '&:hover': { backgroundColor: '#45a049' },
                            }}
                            onClick={handleOpenCreate}
                        >
                            Add Brand
                        </Button>
                    </Box>
                </motion.div>
            </Box>
            <DataGrid
                rows={brands}
                columns={columns}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                autoHeight
                rowHeight={100}
                getRowId={(row) => row.id}
                pagination
                rowCount={totalRows}
                paginationMode="server"
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newPageSize) => setRowsPerPage(newPageSize)}
                sx={{
                    borderRadius: 2,
                    '& .MuiDataGrid-columnHeaders': { bgcolor: '#f9fafb' },
                }}
            />

            <BrandEdit
                open={openEdit}
                handleClose={handleClose}
                brand={selectedBrand}
                onBrandUpdated={handleBrandUpdated}
            />

            <Modal
                open={openCreate}
                onClose={handleCloseCreate}
                aria-labelledby="create-brand-modal-title"
                aria-describedby="create-brand-modal-description"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 650,
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Typography
                            id="create-brand-modal-title"
                            variant="h6"
                            component="h2"
                            sx={{ color: '#2e7d32', fontWeight: "bold" }}
                        >
                            Create New Brand
                        </Typography>
                        <BrandCreate
                            onBrandCreated={handleBrandCreated}
                            handleClose={handleCloseCreate}
                        />
                    </Box>
                </motion.div>
            </Modal>

            <Modal
                open={confirmDelete}
                onClose={handleConfirmDeleteClose}
                aria-labelledby="confirm-delete-modal-title"
                aria-describedby="confirm-delete-modal-description"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Typography id="confirm-delete-modal-title" variant="h6" component="h2" sx={{ color: 'red' }}>
                            Confirm Deletion
                        </Typography>
                        <Typography id="confirm-delete-modal-description" variant="body1">
                            Are you sure you want to delete {selectedRows.length} selected brand(s)?
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                                variant="outlined"
                                onClick={handleConfirmDeleteClose}
                                sx={{ color: '#9ca3af', borderColor: '#9ca3af' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleBulkDelete}
                                sx={{ backgroundColor: 'red', '&:hover': { backgroundColor: 'darkred' } }}
                            >
                                Delete
                            </Button>
                        </Box>
                    </Box>
                </motion.div>
            </Modal>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    </motion.div>
);
};

export default BrandList;